import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Copy, Plus } from "lucide-react";
import styles from "./SeriesTab.module.css";
import {
  Alert,
  Button,
  EmptyState,
  FormModal,
  Input,
  SegmentedControl,
  Select,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifyError, notifySuccess } from "@marekl/adminer/api";
import { useConfirm } from "@marekl/adminer/hooks";
import { useCurrentCompany } from "../../lib/company";
import { DOC_TYPE_LABEL, type DocumentType } from "../../lib/queries/documents";
import {
  useCreateSeries,
  useDeleteSeries,
  useNumberSeries,
  useUpdateSeries,
  type NumberSeries,
} from "../../lib/queries/series";
import { todayCivil } from "../../lib/dates";

const DOC_TYPES = Object.keys(DOC_TYPE_LABEL) as DocumentType[];

/**
 * A format's number for a seq, as the server forms it (numbering.Format):
 * {YYYY}, {YY} and one {N…} padded to its width. For the preview only.
 */
function sampleNumber(format: string, year: number, seq: number): string {
  return format
    .replace(/\{YYYY\}/g, String(year))
    .replace(/\{YY\}/g, String(year).slice(2))
    .replace(/\{(N+)\}/g, (_, n: string) => String(seq).padStart(n.length, "0"));
}

interface SeriesForm {
  doc_type: DocumentType;
  year: string;
  name: string;
  format: string;
}

const TOKENS: { token: string; label: string }[] = [
  { token: "{YY}", label: "rok, 2 číslice" },
  { token: "{YYYY}", label: "rok" },
  { token: "{NNN}", label: "pořadí, 3 číslice" },
  { token: "{NNNN}", label: "pořadí, 4 číslice" },
];

function SeriesModal({ series, onClose }: { series?: NumberSeries; onClose: () => void }) {
  const { id } = useCurrentCompany();
  const create = useCreateSeries(id);
  const update = useUpdateSeries(id);
  const year = Number(todayCivil().slice(0, 4));
  const { register, handleSubmit, setError, setValue, watch, formState } = useForm<SeriesForm>({
    mode: "onTouched",
    defaultValues: series
      ? { doc_type: series.doc_type, year: String(series.year), name: series.name, format: series.format }
      : { doc_type: "sales_invoice", year: String(year), name: "", format: "FV{YY}-{NNN}" },
  });
  const [format, y] = watch(["format", "year"]);
  const frozen = !!series && series.last_seq > 0;
  const first = (series?.last_seq ?? 0) + 1;
  const sample = (seq: number) => sampleNumber(format ?? "", Number(y) || year, seq);

  async function onSubmit(v: SeriesForm) {
    try {
      if (series) await update.mutateAsync({ id: series.id, input: { name: v.name, format: v.format } });
      else await create.mutateAsync({ doc_type: v.doc_type, year: Number(v.year), name: v.name, format: v.format });
      notifySuccess("Číselná řada byla uložena.");
      onClose();
    } catch (err) {
      apiErrorToFormErrors<SeriesForm>(err, setError, { fallbackMessage: "Uložení se nepovedlo." });
    }
  }

  return (
    <FormModal
      open
      title={series ? "Číselná řada" : "Nová číselná řada"}
      submitLabel={series ? "uložit" : "vytvořit řadu"}
      busy={create.isPending || update.isPending}
      error={rootErrorMessage(formState.errors)}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
    >
      <div className={styles.typeYear}>
        <Select label="Typ dokladu" disabled={!!series} {...register("doc_type")}>
          {DOC_TYPES.map((t) => (
            <option key={t} value={t}>{DOC_TYPE_LABEL[t]}</option>
          ))}
        </Select>
        <Input label="Rok" type="number" min={2000} max={2099} disabled={!!series} {...register("year", { required: "Povinné pole" })} error={formState.errors.year?.message} />
      </div>
      <Input
        label="Formát čísla"
        required
        disabled={frozen}
        className={styles.format}
        {...register("format", { required: "Povinné pole" })}
        error={formState.errors.format?.message}
        hint={frozen ? "Z řady už byla vydána čísla, formát nelze měnit." : undefined}
      />
      {!frozen && (
        <div className={styles.tokens}>
          {TOKENS.map((t) => (
            <Button key={t.token} type="button" size="sm" weight="soft" onClick={() => setValue("format", `${format ?? ""}${t.token}`, { shouldDirty: true, shouldValidate: true })}>
              <code>{t.token}</code> {t.label}
            </Button>
          ))}
        </div>
      )}
      <div className={styles.preview}>
        <span className={styles.previewLabel}>Náhled</span>
        <span className={styles.previewNumbers}>
          <b>{sample(first)}</b>
          <Text variant="muted">
            {sample(first + 1)} · {sample(first + 2)} · …
          </Text>
        </span>
      </div>
      <Input label="Název" {...register("name")} hint="Nepovinné, jen pro vaši orientaci." />
    </FormModal>
  );
}

/** Number series by year, with "copy last year's series". */
export function SeriesTab() {
  const { id, can } = useCurrentCompany();
  const series = useNumberSeries(id);
  const create = useCreateSeries(id);
  const del = useDeleteSeries(id);
  const confirm = useConfirm();
  const [modal, setModal] = useState<{ series?: NumberSeries } | null>(null);
  const thisYear = Number(todayCivil().slice(0, 4));
  const manager = can("manager");
  const years = useMemo(
    () => Array.from(new Set([thisYear, ...(series.data ?? []).map((s) => s.year)])).sort((a, b) => b - a),
    [series.data, thisYear],
  );
  const [year, setYear] = useState(thisYear);

  const missingThisYear = useMemo(() => {
    const all = series.data ?? [];
    const have = new Set(all.filter((s) => s.year === thisYear).map((s) => s.doc_type));
    return all.filter((s) => s.year === thisYear - 1 && !have.has(s.doc_type));
  }, [series.data, thisYear]);

  async function copyLastYear() {
    try {
      for (const s of missingThisYear) {
        await create.mutateAsync({ doc_type: s.doc_type, year: thisYear, name: s.name, format: s.format });
      }
      notifySuccess(`Řady pro rok ${thisYear} byly založeny.`);
    } catch (err) {
      notifyError(err);
    }
  }

  const columns: Column<NumberSeries>[] = [
    { key: "type", header: "Doklad", primary: true, render: (s) => DOC_TYPE_LABEL[s.doc_type] },
    { key: "format", header: "Formát", render: (s) => <Text variant="mono">{s.format}</Text> },
    {
      key: "next",
      header: "Další číslo",
      render: (s) => <span className={styles.pill}>{sampleNumber(s.format, s.year, s.last_seq + 1)}</span>,
    },
    { key: "name", header: "Název", render: (s) => s.name || <Text variant="muted">—</Text> },
    {
      key: "actions",
      header: "",
      render: (s) =>
        manager && (
          <Table.Actions>
            <Table.Action onClick={() => setModal({ series: s })}>upravit</Table.Action>
            {s.last_seq === 0 && (
              <Table.Action
                variant="danger"
                onClick={async () => {
                  if (await confirm({ title: "Smazat číselnou řadu", description: `${DOC_TYPE_LABEL[s.doc_type]} ${s.year} (${s.format})`, confirmLabel: "Smazat", variant: "danger" })) {
                    del.mutate(s.id);
                  }
                }}
              >
                smazat
              </Table.Action>
            )}
          </Table.Actions>
        ),
    },
  ];

  return (
    <>
      <div className={styles.tab}>
        {missingThisYear.length > 0 && (
          <Alert variant="warn">
            <div className={styles.missing}>
              <span>
                Pro rok {thisYear} chybí řady: {missingThisYear.map((s) => DOC_TYPE_LABEL[s.doc_type]).join(", ")}. Bez řady nelze doklad vystavit.
              </span>
              {manager && (
                <Button size="sm" weight="outline" loading={create.isPending} startIcon={<Copy size={14} aria-hidden="true" />} onClick={copyLastYear}>
                  převzít z roku {thisYear - 1}
                </Button>
              )}
            </div>
          </Alert>
        )}
        <div className={styles.toolbar}>
          <SegmentedControl
            size="sm"
            aria-label="Rok"
            options={years.map((y) => ({ value: String(y), label: String(y) }))}
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
          />
          {manager && (
            <Button size="sm" variant="primary" startIcon={<Plus size={14} aria-hidden="true" />} onClick={() => setModal({})}>
              nová řada
            </Button>
          )}
        </div>
      </div>
      <Table<NumberSeries>
        cards
        columns={columns}
        rows={(series.data ?? []).filter((s) => s.year === year)}
        rowKey={(s) => s.id}
        isLoading={series.isLoading}
        error={series.error}
        onRetry={series.refetch}
        empty={<EmptyState title={`Pro rok ${year} nejsou číselné řady`} description="Bez řady nelze doklad vystavit." />}
      />
      {modal && <SeriesModal series={modal.series} onClose={() => setModal(null)} />}
    </>
  );
}
